import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { IconButton, Stack, Typography, useTheme } from '@mui/material'
import { StyledLink } from 'styles/utilities'

interface ArrayLinkItem {
  [key: string]: any
}

interface ArrayLinkProps {
  /** Array of objects to render as individual links */
  items?: ArrayLinkItem[] | null
  /** Key to display from each item (default: 'email') */
  displayKey?: string
  /** Function returning the route path for each item */
  route?: (item: ArrayLinkItem) => string

  /** Single string value — rendered as plain text, or as a link if `to` is provided */
  value?: string | null
  /** Internal route for a single-value link */
  to?: string
  /** Open link in new tab */
  external?: boolean
  /** Override display text for a link (e.g. "View" instead of the URL) */
  displayText?: string

  /** Show a copy button next to the link */
  copyable?: boolean
  /** Whether the copy action just fired */
  isCopied?: boolean
  /** Callback when the copy button is clicked */
  onCopy?: () => void

  /** Text shown when there is no data (default: "—") */
  emptyText?: string
}

const ArrayLink = ({
  items,
  displayKey = 'email',
  route,
  value,
  to,
  external,
  displayText,
  copyable,
  isCopied,
  onCopy,
  emptyText = '—',
}: ArrayLinkProps) => {
  const theme = useTheme()

  // ── Array of linked items ──
  if (items && items.length > 0 && route) {
    return (
      <>
        {items.map((item, idx) => (
          <StyledLink
            key={item.id ?? idx}
            to={route(item)}
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item[displayKey]}
          </StyledLink>
        ))}
      </>
    )
  }

  // ── Single link with optional copy button ──
  if (value && to) {
    const linkProps = external ? { to, target: '_blank' as const, rel: 'noopener noreferrer' } : { to }
    const label = isCopied ? 'Copied' : (displayText || value)

    if (copyable) {
      return (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <StyledLink {...linkProps}>{label}</StyledLink>
          <IconButton
            size="small"
            onClick={() => (!isCopied ? onCopy?.() : undefined)}
            sx={{ color: isCopied ? theme.palette.text.success : theme.palette.text.secondary }}
          >
            {isCopied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Stack>
      )
    }

    return <StyledLink {...linkProps}>{label}</StyledLink>
  }

  // ── Plain string value ──
  if (value) {
    return (
      <Typography
        noWrap
        sx={{ fontWeight: 700, fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {value}
      </Typography>
    )
  }

  // ── Empty state ──
  return <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>{emptyText}</Typography>
}

export default ArrayLink
